import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { name: "", description: "", price: "", image: "", category: "Piante", active: true };

export default function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products/all").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description, price: String(p.price), image: p.image, category: p.category, active: p.active });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/products/${id}`).catch(() => {});
    toast.success("Prodotto eliminato");
    load();
  };

  const field = "border-line bg-sand/70 text-forest";

  return (
    <section data-testid="products-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl text-forest">Prodotti del negozio</h2>
        <button onClick={openNew} data-testid="product-new-button" className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
          <Plus className="h-4 w-4" /> Nuovo prodotto
        </button>
      </div>
      <div className="mt-8 overflow-x-auto rounded-sm border border-line bg-bone">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line font-accent text-[10px] uppercase tracking-[0.2em] text-sage">
              <th className="px-5 py-4">Prodotto</th>
              <th className="hidden px-5 py-4 md:table-cell">Categoria</th>
              <th className="px-5 py-4">Prezzo</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-line/60 last:border-0" data-testid={`product-row-${p.id}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-10 w-10 rounded-sm bg-sand object-cover" />
                    <span className="font-medium text-forest">{p.name}</span>
                  </div>
                </td>
                <td className="hidden px-5 py-4 text-sage md:table-cell">{p.category}</td>
                <td className="px-5 py-4 text-forest">{eur(p.price)}</td>
                <td className="px-5 py-4">
                  <span className={`rounded-full px-3 py-1 font-accent text-[10px] uppercase tracking-[0.15em] ${p.active ? "bg-moss/15 text-moss" : "bg-sand text-sage"}`}>
                    {p.active ? "Attivo" : "Nascosto"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="rounded-full border border-line p-2 text-forest hover:bg-sand" data-testid={`product-edit-${p.id}`} aria-label="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(p.id)} className="rounded-full border border-line p-2 text-destructive hover:bg-sand" data-testid={`product-delete-${p.id}`} aria-label="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <p className="px-5 py-10 text-center text-sage">Nessun prodotto. Crea il primo.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-bone" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 pt-2">
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-2 ${field}`} data-testid="product-form-name" />
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              <div>
                <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Prezzo (€) *</Label>
                <Input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={`mt-2 ${field}`} data-testid="product-form-price" />
              </div>
              <div className="md:col-span-2">
                <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`mt-2 ${field}`} data-testid="product-form-category" />
              </div>
            </div>
            <ImageUpload label="Immagine del prodotto" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="product-form-image" />
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Descrizione</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`mt-2 ${field}`} data-testid="product-form-description" />
            </div>
            <label className="flex items-center gap-3 text-sm text-forest">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-form-active" /> Visibile nel negozio
            </label>
            <button onClick={save} disabled={saving || !form.name || !form.price} data-testid="product-form-save" className="flex items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
